import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { motion, AnimatePresence } from '@/components/framer-motion'
import { Phone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import {
  companiesClient,
  type AvailablePhoneNumber,
  type ListAvailableNumbersParams,
} from '@/lib/api/companies.client'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useOnboarding } from '../onboarding-context'
import { findStepByRoute, nextStep } from '../flow.config'

const SKELETON_COUNT = 5

const listVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.04, delayChildren: 0.02 },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 6, filter: 'blur(8px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.28,
      ease: [0.2, 0.65, 0.3, 1] as const,
    },
  },
}

const MAX_RESULTS = 10
const SEARCH_DEBOUNCE_MS = 350
const MIN_QUERY_LENGTH = 3

export function formatE164Display(e164: string): string {
  // +14155551234 → (415) 555-1234
  const match = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164)
  if (!match) return e164
  return `(${match[1]}) ${match[2]}-${match[3]}`
}

/**
 * Format a partial user-typed US phone into `(XXX) XXX-XXXX`. Mirrors the
 * as-you-type helper used elsewhere (see SimulateLeaseModal / SimulateDelinquencyModal).
 *
 * Strips a leading `1` when the caller pastes an 11-digit `1XXXXXXXXXX` or
 * `+1XXXXXXXXXX`; without this the input renders `(141) 555-1234` and the
 * `contains` search ships `14155551234`, which Twilio's local `contains`
 * matches against the 10-digit form → zero hits.
 */
export function formatPhoneInput(value: string): string {
  let digits = value.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1)
  }
  if (digits.length === 0) return ''
  if (digits.length <= 3) return `(${digits}`
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
}

/**
 * Split the search input into what Twilio understands. Three digits are treated
 * as an area code (fast path, doesn't scan the full inventory); anything else
 * becomes a Twilio `contains` pattern — digits with optional `*` wildcards —
 * so a caller pasting a specific number or a partial like `555-1234` still
 * finds hits.
 */
export function buildSearchParams(
  input: string,
): ListAvailableNumbersParams | null {
  const digits = input.replace(/\D/g, '')
  if (digits.length < MIN_QUERY_LENGTH) return null
  if (digits.length === 3) return { areaCode: digits }
  return { contains: digits }
}

export function NumberStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = useOnboarding()
  const { currentCompany, refetchCurrentCompany } = useAppAuth()

  const alreadyClaimed = currentCompany?.phone ?? null

  const [query, setQuery] = useState('')
  const [results, setResults] = useState<AvailablePhoneNumber[]>([])
  const [selected, setSelected] = useState<AvailablePhoneNumber | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [isClaiming, setIsClaiming] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [claimError, setClaimError] = useState<string | null>(null)

  // Bumped on every keystroke, checked when each request lands so a slow
  // earlier request can't overwrite a newer result set.
  const searchTokenRef = useRef(0)

  // Bottom-fade cue: mirrors the `SidebarContent` pattern (sidebar.tsx:187)
  // so the list signals more-below the same way the sidebar does.
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollDown, setCanScrollDown] = useState(false)
  const checkScroll = () => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1)
  }
  useEffect(() => {
    checkScroll()
  }, [results, isSearching])

  useEffect(() => {
    if (alreadyClaimed || !currentCompany?.id) return
    const params = buildSearchParams(query)
    if (!params) {
      setResults([])
      setSelected(null)
      setSearchError(null)
      setIsSearching(false)
      return
    }

    const token = ++searchTokenRef.current
    setIsSearching(true)
    setSearchError(null)

    const handle = setTimeout(async () => {
      try {
        const { numbers } = await companiesClient.listAvailablePhoneNumbers(
          currentCompany.id,
          params,
        )
        if (token !== searchTokenRef.current) return
        setResults(numbers.slice(0, MAX_RESULTS))
        setSelected(prev =>
          prev && numbers.some(n => n.phoneNumber === prev.phoneNumber)
            ? prev
            : null,
        )
        if (numbers.length === 0) {
          const digits = query.replace(/\D/g, '')
          setSearchError(
            `No numbers matched "${digits}". Try a different area code or pattern.`,
          )
        }
      } catch (err) {
        if (token !== searchTokenRef.current) return
        console.error('Phone number search failed', err)
        setSearchError("Couldn't search for numbers. Try again in a moment.")
      } finally {
        if (token === searchTokenRef.current) setIsSearching(false)
      }
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(handle)
  }, [query, currentCompany?.id, alreadyClaimed])

  const advanceToNextStep = () => {
    const current = findStepByRoute(location.pathname)
    const next = current ? nextStep(current, state.userType ?? null) : null
    if (next) navigate(next.route)
  }

  const handleContinue = async () => {
    if (alreadyClaimed) {
      advanceToNextStep()
      return
    }
    if (!selected || !currentCompany?.id || isClaiming) return
    setIsClaiming(true)
    setClaimError(null)
    try {
      await companiesClient.provisionPhoneNumber(
        currentCompany.id,
        selected.phoneNumber,
      )
      await refetchCurrentCompany()
      toast.success(`Claimed ${formatE164Display(selected.phoneNumber)}`)
      advanceToNextStep()
    } catch (err) {
      console.error('Phone number claim failed', err)
      const message =
        err instanceof Error
          ? err.message
          : "Couldn't claim that number. Try another."
      setClaimError(message)
    } finally {
      setIsClaiming(false)
    }
  }

  const canContinue =
    alreadyClaimed != null || (selected != null && !isClaiming)

  return (
    <div className="mx-auto flex w-full max-w-[640px] flex-col gap-6">
      <div className="space-y-2 text-center">
        <p className="text-muted-foreground font-fragment-mono text-[10px] uppercase tracking-[0.14em]">
          SUPER · NUMBER
        </p>
        <h1 className="font-space-grotesk text-3xl font-semibold tracking-tight">
          Pick your Super number
        </h1>
        <p className="text-muted-foreground text-sm">
          {/* TODO(design): §"3. Number" accent callout copy */}
          This is your number for inbound and outbound calls and texts.
        </p>
      </div>

      {alreadyClaimed ? (
        <div className="border-border rounded-md border p-4">
          <p className="text-muted-foreground text-xs uppercase tracking-wider">
            Your number
          </p>
          <p className="mt-1 font-space-grotesk text-2xl font-semibold">
            {formatE164Display(alreadyClaimed)}
          </p>
          <p className="text-muted-foreground mt-2 text-sm">
            Already claimed. Continue when you're ready.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="ml-3 text-xs font-medium">
              Area code or number pattern
            </span>
            <Input
              value={query}
              onChange={e => setQuery(formatPhoneInput(e.target.value))}
              inputMode="tel"
              placeholder="(555) 867-5309"
              maxLength={14}
              autoFocus
            />
            {/* Reserve the status slot so it never pushes the results list
                when the message appears/disappears (mirrors FormFieldWrapper). */}
            <div className="relative min-h-4">
              <span
                className={`text-muted-foreground absolute inset-0 ml-3 text-xs transition-all duration-200 ease-in-out ${
                  isSearching
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 -translate-y-1 pointer-events-none'
                }`}
              >
                Searching…
              </span>
              <span
                className={`text-destructive absolute inset-0 ml-3 text-xs transition-all duration-200 ease-in-out ${
                  searchError && !isSearching
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 -translate-y-1 pointer-events-none'
                }`}
              >
                {searchError}
              </span>
            </div>
          </label>

          {/* Fixed height so the layout below never jumps when swapping
              between empty / skeleton / results. Content scrolls
              internally when results exceed the height, with a bottom
              fade cue mirroring the sidebar pattern. */}
          <div className="relative h-[380px]">
            <div
              ref={scrollRef}
              onScroll={checkScroll}
              role="radiogroup"
              aria-label="Available phone numbers"
              className={`h-full overflow-y-auto pr-1 transition-opacity duration-200 ${
                isSearching && results.length > 0 ? 'opacity-50' : 'opacity-100'
              }`}
            >
              <AnimatePresence mode="wait" initial={false}>
                {/* Show the skeleton state only on the first search into an
                    empty list. Once we have results, subsequent debounced
                    refetches keep the list visible — the "Searching…" text
                    in the reserved status slot signals the refresh instead.
                    Otherwise the list would swap to skeleton on every
                    keystroke, which reads as a flicker. */}
                {isSearching && results.length === 0 ? (
                  <motion.div
                    key="skeleton"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="flex flex-col gap-2"
                  >
                    {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                      <div
                        key={i}
                        className="border-border flex items-start gap-3 rounded-md border p-3"
                      >
                        <Skeleton className="mt-1 h-4 w-4 shrink-0 rounded-full" />
                        <div className="flex flex-1 flex-col gap-2">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-24" />
                        </div>
                      </div>
                    ))}
                  </motion.div>
                ) : (
                  <motion.div
                    key="results"
                    variants={listVariants}
                    initial="hidden"
                    animate="show"
                    exit={{ opacity: 0 }}
                    className="flex flex-col gap-2"
                  >
                    {results.map(number => {
                      const isSelected =
                        selected?.phoneNumber === number.phoneNumber
                      return (
                        <motion.label
                          key={number.phoneNumber}
                          variants={itemVariants}
                          className={`border-border flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                            isSelected
                              ? 'border-primary bg-primary/5'
                              : 'hover:bg-muted/40'
                          }`}
                        >
                          <input
                            type="radio"
                            name="phone-number"
                            value={number.phoneNumber}
                            checked={isSelected}
                            onChange={() => {
                              setSelected(number)
                              setClaimError(null)
                            }}
                            className="mt-1"
                          />
                          <div className="flex flex-col">
                            <span className="font-space-grotesk text-base font-semibold">
                              {formatE164Display(number.phoneNumber)}
                            </span>
                            {(number.locality || number.region) && (
                              <span className="text-muted-foreground text-xs">
                                {[number.locality, number.region]
                                  .filter(Boolean)
                                  .join(', ')}
                              </span>
                            )}
                          </div>
                        </motion.label>
                      )
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div
              aria-hidden="true"
              className={`pointer-events-none absolute bottom-0 left-0 right-0 h-10 transition-opacity duration-200 ${
                canScrollDown ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                background:
                  'linear-gradient(0deg, var(--color-background) 0%, transparent 100%)',
              }}
            />
          </div>

          {claimError && (
            <p className="text-destructive text-sm">{claimError}</p>
          )}
        </div>
      )}

      <FloatingActionIslandContent
        show={true}
        componentId="onboarding-number"
        priority={10}
      >
        <div className="pointer-events-auto flex items-center gap-3">
          <span className="text-muted-foreground text-xs whitespace-nowrap">
            Choose carefully — you only get one save.
          </span>
          <Button onClick={handleContinue} disabled={!canContinue} size="sm">
            <Phone className="mr-1.5 h-3.5 w-3.5" />
            {isClaiming ? 'Saving…' : 'Save my number'}
          </Button>
        </div>
      </FloatingActionIslandContent>
    </div>
  )
}
